import type { Query } from '@tanstack/vue-query'
import { useApi } from '~/api'
import type { BehaviourCreatePayload, Group, GroupAssetsQueryParams } from '~/types/group'
import type { GroupPresetStatus, GroupPresetSyncPayload } from '~/types/configurationPreset'
import { isGroupPresetEnabled, isGroupPresetSyncEnabled } from '~/composables/useFeatureFlags'
import { defineService } from '~/api/models/helpers'
import type { WithId } from '~/types/common'
import type { AuthArgs } from '~/api/models/index'
import type { GroupQueryParams } from '~/types/pagination'

const PRESET_POLL_INTERVAL_MS = 60000

const GROUP_MEMBERSHIP_CACHE_KEYS = [
  ['groupAssets'],
  ['groupAssetCount'],
  ['groupDetails'],
  ['groupPresetStatus'],
  ['allGroups'],
  ['groupedAssetsCount'],
  ['assetById']
]

const BEHAVIOUR_PRESET_CACHE_KEYS = [
  ['allConfigurationPresets'],
  ['groupDetails'],
  ['groupPresetStatus'],
  ['assetById'],
  ['allGroups']
]

const queries = {
  getAll: {
    queryFn: () => useApi().groups.getAllGroups(),
    queryKey: () => ['allGroups']
  },
  getAllPaginated: {
    queryFn: (pagination: Ref<GroupQueryParams>) => useApi().groups.getGroupsPaginated({ ...pagination.value }),
    queryKey: (pagination: Ref<GroupQueryParams>) => [
      'allGroups',
      'paginated',
      {
        current_page: pagination.value.current_page,
        per_page: pagination.value.per_page,
        sort: pagination.value.sort,
        search_terms: pagination.value.search_terms,
        site_id: pagination.value.site_id
      }
    ],
    options: {
      staleTime: () => 1000 * 60 * 5,
      refetchOnMount: () => true
    }
  },
  getAllByAssetType: {
    queryFn: (args: Ref<{ assetTypeIds?: string[]; siteId?: number }>) =>
      useApi().groups.getAllGroups(args.value.assetTypeIds, args.value.siteId),
    queryKey: (args: Ref<{ assetTypeIds?: string[]; siteId?: number }>) => [
      'allGroups',
      args.value.assetTypeIds?.join(',') || 'all',
      { siteId: args.value.siteId }
    ]
  },
  getAssets: {
    queryFn: (args: Ref<GroupAssetsQueryParams & { groupId: number }>) =>
      useApi().groups.getAssetsByGroupId(args.value.groupId, args.value),
    queryKey: (args: Ref<GroupAssetsQueryParams & { groupId: number }>) => [
      'groupAssets',
      {
        groupId: args.value.groupId,
        current_page: args.value.current_page,
        per_page: args.value.per_page,
        sort: args.value.sort,
        preset_state: args.value.preset_state ?? []
      }
    ]
  },
  getAssetCount: {
    queryFn: (args: Ref<{ groupId: number }>) =>
      useApi().groups.getAssetsByGroupId(args.value.groupId, { current_page: 1, per_page: 1 }),
    queryKey: (args: Ref<{ groupId: number }>) => ['groupAssetCount', String(args.value.groupId)]
  },
  getDetails: {
    queryFn: (args: Ref<{ id: number }>) => useApi().groups.getGroupDetails(args.value.id),
    queryKey: (args: Ref<{ id: number }>) => ['groupDetails', String(args.value.id)]
  },
  getPresetStatus: {
    queryFn: (args: Ref<{ id: number }>) => useApi().groups.getGroupPresetStatus(args.value.id),
    queryKey: (args: Ref<{ id: number }>) => ['groupPresetStatus', String(args.value.id)],
    options: {
      enabled: () => isGroupPresetEnabled && isGroupPresetSyncEnabled,
      staleTime: () => 1000 * 60,
      refetchInterval:
        () =>
        (query: Query<GroupPresetStatus[]>): number | false =>
          query.state.data?.some((status) => status.inProgress > 0) ? PRESET_POLL_INTERVAL_MS : false
    }
  }
} as const

const mutations = {
  create: {
    request: (payload: Partial<Group>) => useApi().groups.createGroup(payload),
    cacheKey: () => ['allGroups']
  },
  update: {
    request: ({ id, ...payload }: WithId<Partial<Group>>) => useApi().groups.updateGroup(id, payload),
    cacheKey: () => ['allGroups']
  },
  delete: {
    request: (id: number) => useApi().groups.deleteGroup(id),
    cacheKey: () => ['allGroups']
  },
  assignAssets: {
    request: ({ groupId, assetIds }: { groupId: number; assetIds: number[] }) =>
      useApi().groups.assignAssetsToGroup(groupId, assetIds),
    cacheKey: () => GROUP_MEMBERSHIP_CACHE_KEYS
  },
  unassignAssets: {
    request: ({ groupId, assetIds }: { groupId: number; assetIds: number[] }) =>
      useApi().groups.unassignAssetsFromGroup(groupId, assetIds),
    cacheKey: () => GROUP_MEMBERSHIP_CACHE_KEYS
  },
  assignBehaviour: {
    request: (payload: BehaviourCreatePayload) => useApi().groups.createBehaviour(payload),
    cacheKey: () => [
      ['groupDetails'],
      ['allSchedules'],
      ['scheduleById'],
      ['assetsByScheduleId'],
      ['scheduleCapabilitiesById'],
      ['allGroups']
    ]
  },
  dissociateBehaviour: {
    request: (id: number) => useApi().groups.removeBehaviourSchedule(id),
    cacheKey: () => [
      ['groupDetails'],
      ['allSchedules'],
      ['scheduleById'],
      ['assetsByScheduleId'],
      ['scheduleCapabilitiesById'],
      ['allGroups']
    ]
  },
  assignPreset: {
    request: ({
      groupId,
      assetType,
      presetId
    }: Required<Pick<BehaviourCreatePayload, 'groupId' | 'assetType' | 'presetId'>>) =>
      useApi().groups.createBehaviour({ groupId, assetType, presetId, overwrite: true }),
    cacheKey: () => BEHAVIOUR_PRESET_CACHE_KEYS
  },
  removePreset: {
    request: (behaviourId: number) => useApi().groups.removeBehaviourPreset(behaviourId),
    cacheKey: () => BEHAVIOUR_PRESET_CACHE_KEYS
  },
  syncPresets: {
    request: ({ groupId, assetType, force }: GroupPresetSyncPayload) =>
      useApi().groups.syncGroupPresets(groupId, assetType, force),
    cacheKey: () => [['groupPresetStatus'], ['assetById']]
  },
  assignGroupsToAsset: {
    request: ({ assetId, groupIds }: { assetId: number; groupIds: number[] }) =>
      useApi().assets.assignGroupsToAsset(assetId, groupIds), // Note: This replaces all groups assigned to the asset
    cacheKey: () => GROUP_MEMBERSHIP_CACHE_KEYS
  }
} as const

export type GroupService = ReturnType<typeof defineService<typeof queries, typeof mutations>>

export const registerService = (authArgs: AuthArgs): GroupService =>
  defineService(authArgs, {
    queries,
    mutations
  })
